import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, ShoppingBag, Heart, ChevronLeft, ChevronRight, Star, Truck, Shield, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Quick View Modal Component
export const QuickViewModal = ({ product, isOpen, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Track product view
  useEffect(() => {
    if (isOpen && product) {
      // Save to recently viewed
      const viewed = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
      const filtered = viewed.filter(p => p.id !== product.id);
      const updated = [product, ...filtered].slice(0, 10);
      localStorage.setItem('recentlyViewed', JSON.stringify(updated));
      
      // Track analytics
      axios.post(`${API}/analytics/track?event_type=product_view&product_id=${product.id}&product_name=${encodeURIComponent(product.name)}`).catch(() => {});
    }
  }, [isOpen, product]);
  
  if (!isOpen || !product) return null;
  
  const images = product.images?.length > 0 ? product.images : [product.image];
  
  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };
  
  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200">
        <div className="flex flex-col md:flex-row">
          {/* Image Section */}
          <div className="md:w-1/2 relative bg-gradient-to-br from-amber-50 to-teal-50">
            <button 
              onClick={onClose}
              className="absolute top-4 right-4 z-10 p-2 bg-white/80 rounded-full hover:bg-white transition-colors md:hidden"
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Stock Badge */}
            {product.inventory !== undefined && product.inventory <= 5 && product.inventory > 0 && (
              <div className="absolute top-4 left-4 z-10">
                <Badge className="bg-red-500 text-white animate-pulse">
                  Only {product.inventory} left!
                </Badge>
              </div>
            )}
            
            <div className="aspect-square relative">
              <img
                src={images[currentImageIndex]}
                alt={product.name}
                className="w-full h-full object-contain p-8"
              />
              
              {images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 rounded-full hover:bg-white transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 rounded-full hover:bg-white transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>
            
            {/* Image Thumbnails */}
            {images.length > 1 && (
              <div className="flex justify-center gap-2 p-4">
                {images.slice(0, 5).map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition-colors ${
                      idx === currentImageIndex ? 'border-amber-500' : 'border-transparent'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Details Section */}
          <div className="md:w-1/2 p-6 flex flex-col">
            <button 
              onClick={onClose}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 transition-colors hidden md:block"
            >
              <X className="w-6 h-6" />
            </button>
            
            {/* Category */}
            {product.category && (
              <span className="text-sm text-amber-600 font-medium uppercase tracking-wide">
                {product.category}
              </span>
            )}
            
            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-900 mt-2">{product.name}</h2>
            
            {/* Rating */}
            <div className="flex items-center gap-1 mt-2">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
              ))}
              <span className="text-sm text-gray-500 ml-2">(24 reviews)</span>
            </div>
            
            {/* Price */}
            <div className="mt-4 flex items-baseline gap-3">
              <span className="text-3xl font-bold text-amber-600">
                ${product.price?.toFixed(2)}
              </span>
              {product.compare_at_price && product.compare_at_price > product.price && (
                <span className="text-lg text-gray-400 line-through">
                  ${product.compare_at_price.toFixed(2)}
                </span>
              )}
            </div>
            
            {/* Stock Status */}
            <div className="mt-3">
              {product.inStock ? (
                <span className="text-green-600 text-sm font-medium">✓ In Stock</span>
              ) : (
                <span className="text-red-500 text-sm font-medium">Out of Stock</span>
              )}
            </div>
            
            {/* Description */}
            {product.description && (
              <div 
                className="mt-4 text-gray-600 text-sm line-clamp-4"
                dangerouslySetInnerHTML={{ __html: product.description }}
              />
            )}
            
            {/* Trust Badges */}
            <div className="mt-6 grid grid-cols-3 gap-3 text-center">
              <div className="p-2 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 mx-auto text-teal-600" />
                <span className="text-xs text-gray-600 mt-1 block">Free Shipping</span>
              </div>
              <div className="p-2 bg-gray-50 rounded-lg">
                <Shield className="w-5 h-5 mx-auto text-teal-600" />
                <span className="text-xs text-gray-600 mt-1 block">Secure Pay</span>
              </div>
              <div className="p-2 bg-gray-50 rounded-lg">
                <RotateCcw className="w-5 h-5 mx-auto text-teal-600" />
                <span className="text-xs text-gray-600 mt-1 block">30 Day Returns</span>
              </div>
            </div>
            
            {/* CTA Buttons */}
            <div className="mt-6 space-y-3">
              <a
                href={product.link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  axios.post(`${API}/analytics/track?event_type=product_click&product_id=${product.id}&product_name=${encodeURIComponent(product.name)}`).catch(() => {});
                }}
                className="w-full"
              >
                <Button className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white py-6 text-lg">
                  <ShoppingBag className="w-5 h-5 mr-2" />
                  Buy Now
                </Button>
              </a>
              <Button 
                variant="outline" 
                className="w-full border-2 border-amber-500 text-amber-600 hover:bg-amber-50 py-5"
                onClick={() => {
                  // Add to wishlist
                  const wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
                  if (!wishlist.find(p => p.id === product.id)) {
                    wishlist.push(product);
                    localStorage.setItem('wishlist', JSON.stringify(wishlist));
                    alert('Added to wishlist!');
                  }
                }}
              >
                <Heart className="w-5 h-5 mr-2" />
                Add to Wishlist
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Recently Viewed Products Component
export const RecentlyViewed = ({ onProductClick }) => {
  const [products, setProducts] = useState(() => {
    // Initialize from localStorage
    const viewed = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
    return viewed.slice(0, 6);
  });
  
  if (products.length === 0) return null;
  
  return (
    <section className="py-12 bg-gray-50">
      <div className="container mx-auto px-4">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Recently Viewed</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {products.map((product) => (
            <div 
              key={product.id}
              onClick={() => onProductClick(product)}
              className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer overflow-hidden group"
            >
              <div className="aspect-square bg-gradient-to-br from-amber-50 to-teal-50 overflow-hidden">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-contain p-2 group-hover:scale-110 transition-transform"
                />
              </div>
              <div className="p-3">
                <h4 className="text-sm font-medium text-gray-900 truncate">{product.name}</h4>
                <span className="text-amber-600 font-bold">${product.price?.toFixed(2)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Product Recommendations Component
export const ProductRecommendations = ({ currentProduct, allProducts, onProductClick }) => {
  const recommendations = React.useMemo(() => {
    if (!currentProduct || !allProducts) return [];
    
    // Filter products in same category, excluding current product
    const sameCategory = allProducts.filter(
      p => p.category === currentProduct.category && p.id !== currentProduct.id
    );
    
    // If not enough in same category, add from other categories
    let recs = [...sameCategory];
    if (recs.length < 4) {
      const others = allProducts.filter(
        p => p.category !== currentProduct.category && p.id !== currentProduct.id
      );
      recs = [...recs, ...others];
    }
    
    return recs.slice(0, 4);
  }, [currentProduct, allProducts]);
  
  if (recommendations.length === 0) return null;
  
  return (
    <div className="mt-8 pt-8 border-t">
      <h4 className="text-lg font-bold text-gray-900 mb-4">You Might Also Like</h4>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {recommendations.map((product) => (
          <div 
            key={product.id}
            onClick={() => onProductClick(product)}
            className="bg-gray-50 rounded-lg overflow-hidden cursor-pointer group hover:shadow-md transition-shadow"
          >
            <div className="aspect-square overflow-hidden">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-contain p-2 group-hover:scale-110 transition-transform"
              />
            </div>
            <div className="p-2">
              <h5 className="text-xs font-medium text-gray-900 truncate">{product.name}</h5>
              <span className="text-sm font-bold text-amber-600">${product.price?.toFixed(2)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Sticky Shop Now Bar Component
export const StickyShopBar = ({ show }) => {
  if (!show) return null;
  
  return (
    <div className="fixed bottom-10 left-0 right-0 bg-gradient-to-r from-amber-500 to-amber-600 shadow-lg z-40 transform transition-transform duration-300 animate-in slide-in-from-bottom">
      <div className="container mx-auto px-3 sm:px-4 py-2.5 sm:py-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {/* Mobile: shorter text, Desktop: full text */}
          <span className="text-white font-medium text-xs sm:text-base">
            <span className="sm:hidden">✨ Free Shipping!</span>
            <span className="hidden sm:inline">✨ Handcrafted Resin Art</span>
          </span>
          <span className="text-amber-100 text-xs hidden sm:block">Free shipping on ALL Australian orders</span>
        </div>
        <Link
          to="/search"
          className="bg-white text-amber-600 px-4 sm:px-6 py-2 sm:py-2 rounded-full font-bold hover:bg-amber-50 transition-colors flex items-center gap-1 sm:gap-2 text-sm sm:text-base whitespace-nowrap flex-shrink-0 shadow-md"
        >
          <ShoppingBag className="w-4 h-4" />
          <span>Shop Now</span>
        </Link>
      </div>
    </div>
  );
};

// Social Proof Notification Component
export const SocialProofNotification = () => {
  const [notification, setNotification] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  
  const names = ['Emma', 'Sarah', 'Jessica', 'Amy', 'Lisa', 'Michelle', 'Rachel', 'Nicole', 'Lauren', 'Megan'];
  const locations = ['Sydney', 'Melbourne', 'Brisbane', 'Perth', 'Adelaide', 'Gold Coast', 'Canberra'];
  const products = [
    'Butterfly Earrings',
    'Resin Coaster Set',
    'Beaded Pen',
    'Flower Keycharm',
    'Ocean Pendant',
    'Starfish Earrings',
    'Teddy Bear',
    'Hair Clips Set'
  ];
  
  useEffect(() => {
    // Check if user has dismissed notifications
    const dismissed = sessionStorage.getItem('socialProofDismissed');
    if (dismissed) return;
    
    const showNotification = () => {
      const name = names[Math.floor(Math.random() * names.length)];
      const location = locations[Math.floor(Math.random() * locations.length)];
      const product = products[Math.floor(Math.random() * products.length)];
      const timeAgo = Math.floor(Math.random() * 30) + 1;
      
      setNotification({ name, location, product, timeAgo });
      setIsVisible(true);
      
      // Hide after 5 seconds
      setTimeout(() => {
        setIsVisible(false);
      }, 5000);
    };
    
    // Show first notification after 30 seconds
    const initialTimeout = setTimeout(showNotification, 30000);
    
    // Then show every 2-3 minutes (reduced frequency)
    const interval = setInterval(showNotification, 120000 + Math.random() * 60000);
    
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, []);
  
  if (!notification || !isVisible) return null;
  
  return (
    <div className="fixed bottom-28 sm:bottom-24 left-4 z-40 animate-in slide-in-from-left duration-500">
      <div className="bg-white rounded-lg shadow-xl p-3 sm:p-4 max-w-[280px] sm:max-w-xs border border-gray-100">
        <button 
          onClick={() => {
            setIsVisible(false);
            sessionStorage.setItem('socialProofDismissed', 'true');
          }}
          className="absolute -top-2 -right-2 bg-gray-100 rounded-full p-1 hover:bg-gray-200"
        >
          <X className="w-3 h-3 text-gray-500" />
        </button>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-teal-400 rounded-full flex items-center justify-center text-white font-bold">
            {notification.name[0]}
          </div>
          <div>
            <p className="text-sm text-gray-900">
              <span className="font-semibold">{notification.name}</span> from {notification.location}
            </p>
            <p className="text-sm text-gray-600">
              just purchased <span className="font-medium text-amber-600">{notification.product}</span>
            </p>
            <p className="text-xs text-gray-400 mt-1">{notification.timeAgo} minutes ago</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Low Stock Badge Component
export const LowStockBadge = ({ inventory }) => {
  if (inventory === undefined || inventory > 5 || inventory <= 0) return null;
  
  return (
    <Badge className="absolute top-2 left-2 bg-red-500 text-white text-xs animate-pulse z-10">
      🔥 Only {inventory} left!
    </Badge>
  );
};

export default {
  QuickViewModal,
  RecentlyViewed,
  ProductRecommendations,
  StickyShopBar,
  SocialProofNotification,
  LowStockBadge
};
